/**
 * Which cameras an operator is talking to right now (ENG-2136 half-duplex).
 *
 * The live talk-down hook (useLiveTalkDown) registers a hold here while the
 * push-to-talk button is pressed; the live players read it to force that
 * camera's audio muted so the operator never hears themselves back through
 * the camera's microphone. Keyed by hook instance so two talk-down controls
 * can never clear each other's hold.
 */
import { create } from "zustand";

interface TalkingCamerasState {
  /** hook-instance key -> camera id being talked to */
  holds: Record<string, string>;
  setHold: (key: string, cameraId: string | null) => void;
}

export const useTalkingCamerasStore = create<TalkingCamerasState>((set) => ({
  holds: {},
  setHold: (key, cameraId) =>
    set((state) => {
      if (cameraId == null) {
        if (!(key in state.holds)) return state;
        const next = { ...state.holds };
        delete next[key];
        return { holds: next };
      }
      if (state.holds[key] === cameraId) return state;
      return { holds: { ...state.holds, [key]: cameraId } };
    }),
}));

/** True while any talk-down control is holding to talk to this camera. */
export const useIsTalkingToCamera = (
  cameraId: string | number | null | undefined
): boolean =>
  useTalkingCamerasStore((state) =>
    cameraId == null || cameraId === ""
      ? false
      : Object.values(state.holds).includes(String(cameraId))
  );
